// @story HIR-HR9
// ReminderReport — "Fee reminders: who paid afterwards".
// Read-only. Shows, per school, every applicant the "Remind unpaid" button
// has reached: when they were reminded, how many times, and whether and when
// they paid. Data from GET /api/dashboard/reminders (tenant-scoped; a group
// owner sees each of their schools separately). Loaded only when opened, since
// it lists applicants by name and every view is logged.
// "Sent by" (6 Oct): who pressed the button for each reminder. Reminders sent
// before that was recorded show "Not recorded" — never a guessed name.

import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Loader2, RefreshCw } from 'lucide-react';
import api from '../../services/api';

const OUTCOME = {
  'paid-after':        { label: 'Paid after reminder', cls: 'bg-green-50 text-green-700' },
  'paid-before':       { label: 'Had already paid', cls: 'bg-gray-100 text-gray-600' },
  'paid-time-unknown': { label: 'Paid (time not recorded)', cls: 'bg-green-50 text-green-700' },
  waived:              { label: 'Fee waived', cls: 'bg-gray-100 text-gray-600' },
  'not-paid':          { label: 'Not paid yet', cls: 'bg-amber-50 text-amber-800' },
};

const FILTERS = [
  { key: 'all', label: 'Everyone reminded' },
  { key: 'paid', label: 'Paid afterwards' },
  { key: 'unpaid', label: 'Not paid yet' },
];

const when = (iso) => (iso
  ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' })
  : '—');

const afterHours = (h) => {
  if (h === null || h === undefined) return '';
  if (h < 1) return 'within the hour';
  if (h < 48) return `${Math.round(h)} h later`;
  return `${Math.round(h / 24)} days later`;
};

const NOT_RECORDED = 'Not recorded';
const sentByOf = (r) => r.sentBy || NOT_RECORDED;

function toCsv(school) {
  const head = ['School', 'Applicant', 'Application', 'Role', 'First reminded', 'Last reminded', 'Times reminded', 'Sent by', 'Outcome', 'Paid on'];
  const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = school.people.map((p) => [
    school.schoolName, p.name, p.applicationId, p.role, when(p.firstRemindedAt), when(p.lastRemindedAt),
    p.timesReminded, (p.reminders || []).map((r) => `${when(r.at)}: ${sentByOf(r)}`).join('; '),
    OUTCOME[p.outcome]?.label || p.outcome, p.paidAt ? when(p.paidAt) : '',
  ].map(cell).join(','));
  return [head.map(cell).join(','), ...lines].join('\n');
}

function download(school) {
  const blob = new Blob([toCsv(school)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fee-reminders-${(school.schoolName || 'school').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ReminderReport({ refreshKey = 0 }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ phase: 'idle' }); // idle | loading | ready | error
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    setState((s) => ({ ...s, phase: 'loading' }));
    api.get('/dashboard/reminders')
      .then((r) => setState({ phase: 'ready', schools: r.data.schools || [], generatedAt: r.data.generatedAt }))
      .catch(() => setState({ phase: 'error' }));
  }, []);

  useEffect(() => { if (open) load(); }, [open, load, refreshKey]);

  const keep = (p) => {
    if (filter === 'paid') return p.outcome === 'paid-after' || p.outcome === 'paid-time-unknown';
    if (filter === 'unpaid') return p.outcome === 'not-paid';
    return true;
  };

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow p-4">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-2 text-left">
        <span>
          <span className="font-heading font-bold text-[15px]">Fee reminders: who paid afterwards</span>
          <span className="block text-xs text-gray-500">Everyone the “Remind unpaid to pay” button has reached, and whether they paid after it.</span>
        </span>
        {open ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronRight size={16} className="text-gray-500" />}
      </button>

      {open && (
        <div className="mt-3">
          <div className="flex items-center gap-1.5 flex-wrap mb-3">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`px-3 py-1 rounded-dps text-xs border transition ${filter === f.key ? 'border-orange-500 text-orange-600 font-medium' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                {f.label}
              </button>
            ))}
            <button onClick={load} className="p-1 rounded-dps text-gray-500 hover:bg-gray-50 ml-auto" title="Refresh">
              <RefreshCw size={14} className={state.phase === 'loading' ? 'animate-spin' : ''} />
            </button>
          </div>

          {state.phase === 'loading' && !state.schools && (
            <div className="text-sm text-gray-500 flex items-center gap-1.5"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          )}
          {state.phase === 'error' && (
            <div className="text-sm text-red-600">Could not load the reminder report. Try again, or tell your developer.</div>
          )}
          {state.schools && state.schools.length === 0 && (
            <div className="text-sm text-gray-500">No landing-page applicants yet, so no reminders to report.</div>
          )}

          {(state.schools || []).map((s) => {
            const people = s.people.filter(keep);
            return (
              <div key={s.school || 'school'} className="mb-5 last:mb-0">
                <div className="flex items-baseline justify-between gap-2 flex-wrap mb-1.5">
                  <h3 className="font-heading font-bold text-sm">{s.schoolName}</h3>
                  {s.people.length > 0 && (
                    <button onClick={() => download(s)} className="text-xs text-gray-600 hover:text-orange-600 flex items-center gap-1">
                      <Download size={13} /> Download as spreadsheet
                    </button>
                  )}
                </div>
                <div className="text-xs text-gray-600 mb-2">
                  <span className="font-medium text-gray-900">{s.counts.reminded}</span> reminded ·{' '}
                  <span className="font-medium text-green-700">{s.counts.paidAfterReminder}</span> paid afterwards
                  {s.counts.paidAfterReminderPct !== null ? ` (${s.counts.paidAfterReminderPct}%)` : ''} ·{' '}
                  <span className="font-medium text-amber-800">{s.counts.stillUnpaid}</span> not paid yet
                  {s.counts.alreadyPaidWhenReminded ? ` · ${s.counts.alreadyPaidWhenReminded} had already paid` : ''}
                  {s.counts.neverReminded ? ` · ${s.counts.neverReminded} unpaid never reminded` : ''}
                </div>
                {people.length === 0 ? (
                  <div className="text-sm text-gray-500">{s.people.length === 0 ? 'No one has been reminded yet.' : 'No one in this view.'}</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-label text-gray-500 border-b border-gray-200">
                          <th className="py-1.5 pr-3 font-medium">Applicant</th>
                          <th className="py-1.5 pr-3 font-medium">Role</th>
                          <th className="py-1.5 pr-3 font-medium">Reminded</th>
                          <th className="py-1.5 pr-3 font-medium">Sent by</th>
                          <th className="py-1.5 pr-3 font-medium">Paid?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {people.map((p) => (
                          <tr key={p.id} className="border-b border-gray-100 last:border-0 align-top">
                            <td className="py-1.5 pr-3">
                              <div className="text-gray-900">{p.name}</div>
                              {p.applicationId && <div className="text-xs text-gray-500">{p.applicationId}</div>}
                            </td>
                            <td className="py-1.5 pr-3 text-gray-700">{p.role || '—'}</td>
                            <td className="py-1.5 pr-3 text-gray-700">
                              {when(p.firstRemindedAt)}
                              {p.timesReminded > 1 && <div className="text-xs text-gray-500">{p.timesReminded} times, last {when(p.lastRemindedAt)}</div>}
                            </td>
                            <td className="py-1.5 pr-3 text-gray-700">
                              {(p.reminders || []).map((r, i) => (
                                <div key={i} className={p.reminders.length > 1 ? 'text-xs' : ''}>
                                  {p.reminders.length > 1 && <span className="text-gray-500">{when(r.at)}: </span>}
                                  <span className={r.sentBy ? '' : 'italic text-gray-500'}>{sentByOf(r)}</span>
                                </div>
                              ))}
                            </td>
                            <td className="py-1.5 pr-3">
                              <span className={`inline-block rounded-pill px-2 py-0.5 text-xs ${OUTCOME[p.outcome]?.cls || ''}`}>{OUTCOME[p.outcome]?.label || p.outcome}</span>
                              {p.paidAt && <div className="text-xs text-gray-500 mt-0.5">{when(p.paidAt)}{p.hoursToPay !== null ? `, ${afterHours(p.hoursToPay)}` : ''}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {state.schools && state.schools.length > 0 && (
            <p className="text-[11px] text-gray-500 mt-3">
              “Paid afterwards” counts everyone who paid after their first reminder. Some of them would have paid anyway, so read it as the most the reminders could have done.
              Times are India time.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
